import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Download, FileUp, Phone, Play, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { day, del, get, money, post, put, uploadFile } from '../lib/api';
import { Badge, Button, Card, ErrorNote, Input, Loading, PageHeader } from '../ui';
import { formatCustomerId } from './Accounts';
import { DataTable } from '../components/DataTable';

type Detail = {
  account: { id: string; customer_id: string; name: string; currency: string; business_kind: string };
  imports: {
    id: string; kind: string; filename: string | null; period_start: string | null; period_end: string | null;
    row_count: number; skipped_count: number; warnings: string[]; created_at: string;
  }[];
  audits: { id: string; ran_at: string; wasted: number; total_cost: number; summary: string }[];
  needed: { kind: string; label: string }[];
};

export default function Account() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['account', id], queryFn: () => get<Detail>(`/v1/accounts/${id}`) });

  const run = useMutation({
    mutationFn: () => post<{ id: string }>(`/v1/accounts/${id}/audits`),
    onSuccess: (r) => nav(`/audits/${r.id}`),
  });

  const sync = useMutation({
    mutationFn: () => post<{ rows: number; kinds: { kind: string; rows: number }[] }>(`/v1/accounts/${id}/sync`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account', id] }),
  });

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const d = q.data;
  const got = new Set(d.imports.map((i) => i.kind));

  return (
    <>
      <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-sm text-t2 hover:text-t1">
        <ArrowLeft className="size-4" /> Accounts
      </Link>

      <PageHeader
        title={d.account.name}
        subtitle={<span className="num">{formatCustomerId(d.account.customer_id)}</span>}
        actions={
          <>
            <Button loading={sync.isPending} onClick={() => sync.mutate()} title="Pull everything straight from Google">
              <Download className="size-4" />Pull from Google
            </Button>
            <Button variant="primary" loading={run.isPending} disabled={!d.imports.length} onClick={() => run.mutate()}>
              <Play className="size-4" />Run the audit
            </Button>
          </>
        }
      />

      <ErrorNote error={run.error ?? sync.error} />
      {sync.data && (
        <p className="mb-4 rounded-btn border border-bd bg-bg3 px-3 py-2 text-sm">
          Pulled {sync.data.rows} rows: {sync.data.kinds.map((k) => `${k.kind.replace(/_/g, ' ')} ${k.rows}`).join(', ')}.
        </p>
      )}

      {/* The account itself, before anything this tool has to say about it.
          Somebody who works in Google Ads all day should recognise this
          immediately, and every claim the audit makes has its rows two clicks
          away. */}
      {d.imports.length > 0 && (
        <div className="mb-6">
          <DataTable accountId={d.account.id} currency={d.account.currency} />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Dropzone accountId={d.account.id} onDone={() => qc.invalidateQueries({ queryKey: ['account', id] })} />

          <Card pad={false}>
            <div className="p-5 pb-3">
              <h2 className="text-sm font-bold">What has been imported</h2>
              <p className="text-xs text-t3">
                One window per report. Uploading a newer download replaces the old one rather than adding to it.
              </p>
            </div>
            <ul>
              {d.needed.map((n) => {
                const have = d.imports.find((i) => i.kind === n.kind);
                return (
                  <li key={n.kind} className="flex flex-wrap items-center gap-3 border-t border-bd px-5 py-3">
                    <span className={clsx('flex size-5 shrink-0 items-center justify-center rounded-full',
                      have ? 'bg-ok-bg text-ok' : 'border border-dashed border-bd2 text-t3')}>
                      {have ? <Check className="size-3" /> : null}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{n.label}</div>
                      {have ? (
                        <div className="num text-xs text-t3">
                          {have.row_count} rows
                          {have.period_start && <> · {day(have.period_start)} – {day(have.period_end)}</>}
                        </div>
                      ) : (
                        <div className="text-xs text-t3">Not uploaded — the checks that need it will say so.</div>
                      )}
                      {have?.warnings?.map((w) => (
                        <div key={w} className="mt-0.5 text-xs text-warn">{w}</div>
                      ))}
                    </div>
                    {have && (
                      <button
                        title="Remove this import"
                        className="text-t3 hover:text-err"
                        onClick={async () => {
                          await del(`/v1/imports/${have.id}`);
                          qc.invalidateQueries({ queryKey: ['account', id] });
                        }}
                      >
                        <Trash2 className="size-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <h2 className="text-sm font-bold">Audits</h2>
            {!d.audits.length ? (
              <p className="mt-1 text-sm text-t3">None yet.</p>
            ) : (
              <ul className="mt-2 -mx-2">
                {d.audits.map((a) => (
                  <li key={a.id}>
                    <Link to={`/audits/${a.id}`} className="block rounded-lg px-2 py-2 hover:bg-hover">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm">{day(a.ran_at)}</span>
                        <span className="num text-sm font-bold text-err">{money(a.wasted, d.account.currency)}</span>
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-xs text-t3">{a.summary}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <LeadCallLink accountId={d.account.id} onDone={() => qc.invalidateQueries({ queryKey: ['account', id] })} />

          <Card>
            <h2 className="text-sm font-bold">Getting the files</h2>
            <ol className="mt-2 space-y-2 text-xs text-t2">
              <li><b>1.</b> Ask for a read-only manager link. They click Accept in their own account; no password is shared and they can revoke it in two clicks.</li>
              <li><b>2.</b> Set the date range to the last 30 days.</li>
              <li><b>3.</b> Download Campaigns, Search terms, Keywords, Ads and Conversion actions as .csv.</li>
              <li><b>4.</b> Drop them in unedited. The report title row, the <span className="num">--</span> cells and the Total lines are all expected.</li>
            </ol>
          </Card>
        </div>
      </div>
    </>
  );
}

/**
 * The half of the audit nobody else has.
 *
 * A read-only CRM key for one workspace. Not a login and not a shared
 * database: the CRM stays the owner of its own data and can revoke this
 * without anybody touching this service.
 */
function LeadCallLink({ accountId, onDone }: { accountId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ workspace_id: '', api_key: '' });
  const link = useMutation({
    mutationFn: () => put<{ calls_seen: number; never_attended: number }>(
      `/v1/accounts/${accountId}/leadcall`, form,
    ),
    onSuccess: () => { setOpen(false); onDone(); },
  });

  return (
    <Card>
      <h2 className="text-sm font-bold">Did they answer the phone?</h2>
      <p className="mt-0.5 text-sm text-t2">
        Link this account to its LeadCall workspace and the audit can say the sentence no competitor can:
        which hours the ads bought calls that nobody picked up.
      </p>
      {!open ? (
        <Button className="mt-3" onClick={() => setOpen(true)}><Phone className="size-4" />Link LeadCall</Button>
      ) : (
        <form className="mt-3 space-y-2" onSubmit={(e) => { e.preventDefault(); link.mutate(); }}>
          <Input placeholder="CRM workspace ID" value={form.workspace_id}
            onChange={(e) => setForm({ ...form, workspace_id: e.target.value })} />
          <Input placeholder="Read-only API key (leadcall:read)" value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
          <ErrorNote error={link.error} />
          <div className="flex gap-2">
            <Button type="submit" variant="primary" loading={link.isPending}
              disabled={!form.workspace_id.trim() || !form.api_key.trim()}>Link it</Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
          <p className="text-xs text-t3">
            Make the key in the CRM under Settings → API keys, with the leadcall:read scope only. It is checked
            against the CRM before it is stored.
          </p>
        </form>
      )}
      {link.data && (
        <p className="mt-2 text-sm text-ok">
          Linked. {link.data.calls_seen} calls, {link.data.never_attended} people never spoken to.
        </p>
      )}
    </Card>
  );
}

function Dropzone({ accountId, onDone }: { accountId: string; onDone: () => void }) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const input = useRef<HTMLInputElement>(null);

  async function take(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    const lines: string[] = [];
    // One at a time: each upload replaces a window, and two of the same kind
    // arriving together would race to be the survivor.
    for (const file of Array.from(files)) {
      try {
        const r = await uploadFile(accountId, file);
        lines.push(r.duplicate
          ? `${file.name} — already imported, nothing changed`
          : `${file.name} → ${r.kind.replace(/_/g, ' ')}, ${r.rows} rows`);
      } catch (e) {
        lines.push(`${file.name} — ${(e as Error).message}`);
        setError(e);
      }
    }
    setSaid(lines);
    setBusy(false);
    onDone();
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}
      className={clsx(
        'rounded-card border-2 border-dashed p-6 text-center transition-colors',
        over ? 'border-ink bg-hover' : 'border-bd bg-bg2',
      )}
    >
      <FileUp className="mx-auto size-6 text-t3" />
      <p className="mt-2 text-sm font-medium">Drop the downloaded reports here</p>
      <p className="mt-0.5 text-xs text-t3">
        All five at once is fine. Each one is recognised by its own columns — nothing to pick from a list.
      </p>
      <input ref={input} type="file" multiple accept=".csv,.tsv,text/csv" className="hidden"
        onChange={(e) => take(e.target.files)} />
      <Button className="mt-3" loading={busy} onClick={() => input.current?.click()}>Choose files</Button>
      {said.length > 0 && (
        <ul className="mt-3 space-y-0.5 text-left text-xs text-t2">
          {said.map((s) => <li key={s}>{s}</li>)}
        </ul>
      )}
      {error != null && <div className="mt-3 text-left"><ErrorNote error={error} /></div>}
    </div>
  );
}
